import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    inject,
    Injector,
    OnInit,
    signal,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {sortedIndexBy} from 'lodash-es';
import {defer, EMPTY, iif, Observable, Subject} from 'rxjs';
import {exhaustMap, switchMap, takeUntil, tap, withLatestFrom} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {squash} from '../../common/rxjs/squash';
import {toNever} from '../../common/rxjs/to-never';
import {destroy$} from '../../components/destroyable';
import {DexOrderTxSubjectService} from '../../components/dex/dex-streaming/subjects/dex-order-tx-subject-service';
import {DexOrderFromApiStatus} from '../../components/dex/dex-streaming/types/dex-order';
import {Widget} from '../../components/layout/widget/widget';
import {WIDGET_UPDATES_GATING$} from '../../components/layout/widget/widget-updates-gating';
import {PARTIAL_LOADING, PartialLoading} from '../../components/partial-loading-indicator';
import {showAlertOnResponseErrorCodeMsg} from '../../components/response-errors/response-error-code-msg';
import {SelectItem} from '../../components/select-input/select-item';
import {TablePaginationComponent} from '../../components/table-pagination';
import {TableComponent} from '../../components/tables/table';
import {QueryLangFilterOperator} from '../../enums/query-lang-filter-operator';
import {SortDirection} from '../../enums/sort-direction';
import {ListFilterFactory} from '../../factories/list-filter.factory';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {QueryLangFilterCriterion} from '../../models/filters/query-lang-filter-criterion';
import {PaginationInfo} from '../../models/pagination-info';
import {CellSizeObserverService} from '../../pages/dashboard/layout/cell/cell-size-observer.service';
import {WidgetFilter} from '../components/widget-filter/widget-filter';
import {showWidgetInnerAlert} from '../components/widget-inner-alert/utils';
import {
    DEX_TX_HISTORY_TABLE_COLUMNS_DICT,
    DEX_TX_HISTORY_TABLE_COLUMNS_SIZING,
    DexTxHistoryTable,
    DexTxHistoryTableColumnConfig
} from './dex-tx-history-table';
import {DexTxHistoryWidget} from './dex-tx-history-widget';
import {DisplayTxHistoryRec, DisplayTxHistoryRecKey} from './display-tx-history-rec';
import {DisplayTxHistoryFactory} from './display-tx-history.factory';
import {FilterTxByAddressInput} from './filters/filter-tx-by-address-input';
import {FilterTxByHashInput} from './filters/filter-tx-by-hash-input';
import {FilterTxByMultiMethod} from './filters/filter-tx-by-multi-method';
import {FilterTxByMultiStatus} from './filters/filter-tx-by-multi-status';
import {
    DexTxHistoryWidgetFiltersSettings,
    DexTxHistoryWidgetSettings,
    DexTxHistoryWidgetTab
} from './settings/dex-tx-history-widget-settings';
import {TxHistoryStatus} from './tx-history-rec';
import {DexFilterOTxByUserWalletsService} from './tx-storage/dex-filter-otx-by-user-wallets.service';
import {DexOrderTx, DexOrderTxFromApi} from './tx-storage/dex-order-tx';
import {DexOrderTxStorageService} from './tx-storage/dex-order-tx-storage.service';

const EMPTY_ROW = {} as DisplayTxHistoryRec;

const TABS: Array<SelectItem<DexTxHistoryWidgetTab>> = [
    {
        value: DexTxHistoryWidgetTab.RECENT,
        label: 'Recent'
    },
    {
        value: DexTxHistoryWidgetTab.HISTORY,
        label: 'History'
    }
];

@Component({
    selector:        'dex-tx-history-w-content',
    templateUrl:     './dex-tx-history-w-content.component.html',
    styleUrl:        './dex-tx-history-w-content.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone:      false
})
export class DexTxHistoryWContentComponent implements OnInit, AfterViewInit {

    @ViewChild('tableEl', {read: TableComponent, static: false})
    public tableComponent: TableComponent;

    @ViewChild('tablePaginationEl', {read: TablePaginationComponent, static: false})
    public tablePaginationComponent: TablePaginationComponent;

    @ViewChild('loadingFullEl', {read: PARTIAL_LOADING, static: true})
    public fullLoading: PartialLoading;

    @ViewChild('loadingPageEl', {read: PARTIAL_LOADING, static: true})
    public pageLoading: PartialLoading;

    @ViewChild('innerModalAnchorEl', {read: ViewContainerRef, static: true})
    public innerModalAnchorVcRef: ViewContainerRef;

    public readonly TABS = TABS;
    public readonly DexTxHistoryWidgetTab = DexTxHistoryWidgetTab;

    public readonly emptyRow = EMPTY_ROW;

    public readonly destroy$ = destroy$();

    public readonly table: DexTxHistoryTable = {
        columnsDict:    DEX_TX_HISTORY_TABLE_COLUMNS_DICT,
        columnsConfig:  [],
        columnsSizingSteps: DEX_TX_HISTORY_TABLE_COLUMNS_SIZING,
        rows:           [],
        emptyRow:       EMPTY_ROW,
        columnsUpdate$: new Subject<DexTxHistoryTableColumnConfig[]>()
    };

    public readonly resize$ = inject(CellSizeObserverService).resize$;

    public readonly refresh$ = new Subject<void>();
    public readonly paginationChanged$ = new Subject<void>();

    public readonly filterByAddress = new FilterTxByAddressInput();
    public readonly filterRecentByAddress = new FilterTxByAddressInput();

    public listInfo: PaginationInfo;

    private readonly widget = inject(Widget) as DexTxHistoryWidget;
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly displayTxHistoryFactory = inject(DisplayTxHistoryFactory);
    private readonly injector = inject(Injector);

    private readonly dexOrderTxStorageService = inject(DexOrderTxStorageService);
    private readonly dexOrderTxSubjectService = inject(DexOrderTxSubjectService);
    private readonly dexFilterOTxByUserWalletsService = inject(DexFilterOTxByUserWalletsService);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly widgetFilter = new WidgetFilter<DexTxHistoryWidgetFiltersSettings>(this.widget, {
        hash: new FilterTxByHashInput(),
        status: new FilterTxByMultiStatus(),
        address: this.filterByAddress,
        method: new FilterTxByMultiMethod()
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly listFilter = ListFilterFactory.createWithSearchFields(
        this.widgetFilter.getFilterCriterion(),
        'createdAt',
        SortDirection.DESC
    );

    private readonly widgetImpl = this.widget.implementationInstance;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly $currentTab = signal(this.widgetImpl.settings?.tab);

    private readonly settingsWithUpdates$: Observable<Partial<DexTxHistoryWidgetSettings>> =
                this.widget.settingsWithUpdates$.pipe(
                    takeUntil(this.destroy$)
                );

    private readonly destroyAlertSubject$ = new Subject<void>();

    private recentRows: DisplayTxHistoryRec[] | undefined;
    private historyRows: DisplayTxHistoryRec[] | undefined;

    private readonly recentRowLimit = 50;

    private readonly _tabEffect = effect(() => {
        const tab = this.$currentTab();
        const knownRows = tab === DexTxHistoryWidgetTab.RECENT
            ? this.recentRows
            : this.historyRows;

        if (knownRows) {
            this.table.rows = knownRows;
        } else {
            this.table.rows = [];
            this.refresh$.next();
        }

        this.changeDetectorRef.markForCheck();
    });

    public ngOnInit(): void {
        this.initFetching();
        this.listenToSettingsChanges();
        this.listenToTableEvents();
        this.bindFiltersAndSettings();
        this.listenToStorage();
    }

    public ngAfterViewInit(): void {
        this.listenToTableSize();
    }

    public onRowClick(row: DisplayTxHistoryRec, event: MouseEvent): void {
        if (row?.raw) {
            event.preventDefault();
            event.stopPropagation();

            // TODO
            // this.floatWidgetFactory.createFloatWidget<BackofficeUserDetailsWidgetSettingsDefinition>(
            //     BACKOFFICE_USER_DETAILS_WIDGET_TYPE_ID,
            //     {
            //         userId: row.userSession.userId,
            //         user:   row.userSession.user as BackofficeUser
            //     }
            // );
        }
    }

    public getTypedRow(row: DisplayTxHistoryRec): DisplayTxHistoryRec {
        return row;
    }

    public onSelectTab(tab: DexTxHistoryWidgetTab, event: MouseEvent): void {
        event.preventDefault();
        this.$currentTab.set(tab);

        this.widgetImpl.updateSettings({tab}).subscribe();
    }

    private onFilterChange(): void {
        if (this.$currentTab() === DexTxHistoryWidgetTab.HISTORY) {
            this.listFilter.pageIndex = 0;
            this.refresh$.next();
        }
    }

    private listenToSettingsChanges(): void {
        this.settingsWithUpdates$.subscribe(settings => {
            let hasChanges: boolean = false;

            if (settings.columns) {
                this.table.columnsConfig = settings.columns;
                hasChanges = true;
            }

            if (settings.columnsResizingMode !== undefined) {
                this.table.columnsSizingMode = settings.columnsResizingMode;
                hasChanges = true;
            }

            if (settings.tab) {
                this.$currentTab.set(settings.tab);
            }

            if (hasChanges) {
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    private initFetching(): void {
        const loading$ = iif(
            () =>
                this.$currentTab() === DexTxHistoryWidgetTab.RECENT
                || this.listInfo && this.listInfo.pageSize === this.listFilter.pageSize,
            this.pageLoading.loading$,
            this.fullLoading.loading$
        );

        this.refresh$.pipe(
            exhaustMap(() => {
                this.destroyAlertSubject$.next();

                const req$ = this.$currentTab() === DexTxHistoryWidgetTab.RECENT
                    ? this.fetchRecent()
                    : this.fetchHistory();

                return req$.pipe(
                    bindWith(loading$),
                    showAlertOnResponseErrorCodeMsg({
                        injector: this.injector,
                        vcRef:    this.innerModalAnchorVcRef,
                        alertFn:  showWidgetInnerAlert,

                        destroyAlertSubject$: this.destroyAlertSubject$
                    })
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private getTableRowIndex(
        oTx: DexOrderTx | undefined,
        key?: DisplayTxHistoryRecKey,
        allRows: DisplayTxHistoryRec[] = this.table.rows
    ): number {
        key ??= this.displayTxHistoryFactory.createRowKeyForOrderTx(oTx);

        return allRows.findIndex(r => this.displayTxHistoryFactory.compareRowKeys(r.key, key));
    }

    private getRowSortedIndex(allRows: DisplayTxHistoryRec[], row: DisplayTxHistoryRec): number {
        return sortedIndexBy(allRows, row, r => -r.timestamp);
    }

    private addOrUpdateSingle(oTx: DexOrderTx): void {
        let hasChanges: boolean = false;
        const row = this.displayTxHistoryFactory.create(oTx);

        const updateSingleTab = (allRows: DisplayTxHistoryRec[], isRecent: boolean = false): void => {
            if (allRows) {
                const index = this.getTableRowIndex(undefined, row.key, allRows);

                if (index >= 0) {
                    allRows[index] = this.displayTxHistoryFactory.create(oTx);
                    hasChanges = true;
                } else if (isRecent) {
                    const row = this.displayTxHistoryFactory.create(oTx);
                    const index = this.getRowSortedIndex(allRows, row);
                    allRows.splice(index, 0, row);

                    if (allRows.length >= this.recentRowLimit) {
                        allRows.pop();
                    }

                    hasChanges = true;
                }
            } else {
                console.warn('[E] add/update while empty'); // TODO
            }
        };

        updateSingleTab(this.recentRows, true);
        updateSingleTab(this.historyRows);

        if (hasChanges) {
            this.changeDetectorRef.markForCheck();
        }
    }

    private createRecentListFilters(): {
        active: CommonListFilter,
        others: CommonListFilter
    } {
        const sortColumn: keyof DexOrderTxFromApi = 'createdAt';
        const statusFieldNames = ['transaction.status', 'order.status'];
        const activeFilter = new QueryLangFilterCriterion({
            fieldNames: statusFieldNames,
            operator: QueryLangFilterOperator.IN,
            query: [
                TxHistoryStatus.PENDING, TxHistoryStatus.CONFIRMED,
                DexOrderFromApiStatus.pending, DexOrderFromApiStatus.accepted
            ]
        });
        const othersFilter = new QueryLangFilterCriterion({
            fieldNames: statusFieldNames,
            operator: QueryLangFilterOperator.IN,
            query: [
                TxHistoryStatus.DONE, TxHistoryStatus.FAILED,
                DexOrderFromApiStatus.executed
            ]
        });

        return {
            active: ListFilterFactory.createWithSearchFields(
                [activeFilter, this.filterRecentByAddress],
                sortColumn,
                SortDirection.DESC,
                100
            ),
            others: ListFilterFactory.createWithSearchFields(
                [othersFilter, this.filterRecentByAddress],
                sortColumn,
                SortDirection.DESC,
                100
            )
        };
    }

    private fetchRecent(): Observable<never> {
        return defer(() => {
            const listFilters = this.createRecentListFilters();

            let fetchedRows: DisplayTxHistoryRec[] | undefined = undefined;
            const storeRows = () => {
                this.recentRows = fetchedRows;
                this.shareRowsFromRecentToHistory();
            };

            return this.dexOrderTxStorageService.fetchOrderTransactions(listFilters.active).pipe(
                switchMap(({data}) => {
                    fetchedRows = this.displayTxHistoryFactory.createAll(data);
                    this.table.rows = fetchedRows;
                    this.changeDetectorRef.markForCheck();

                    if (data.length >= this.recentRowLimit) {
                        storeRows();

                        return EMPTY;
                    }

                    listFilters.others.pageSize = Math.min(this.recentRowLimit + data.length, 100);

                    return this.dexOrderTxStorageService.fetchOrderTransactions(listFilters.others);
                }),
                tap(({data}) => {
                    if (data.length) {
                        this.addMissingRowsWithLimit(fetchedRows, data);
                    }

                    this.changeDetectorRef.markForCheck();
                    storeRows();
                }),
                toNever()
            );
        });
    }

    private fetchHistory(): Observable<never> {
        return this.dexOrderTxStorageService.fetchOrderTransactions(this.listFilter).pipe(
            tap(({data, listInfo}) => {
                this.historyRows = this.displayTxHistoryFactory.createAll(data);
                this.table.rows = this.historyRows;
                this.listInfo = listInfo;
                this.changeDetectorRef.markForCheck();
            }),
            toNever()
        );
    }

    private addMissingRowsWithLimit(allRows: DisplayTxHistoryRec[], oTxs: DexOrderTx[]): void {
        for (const oTx of oTxs) {
            const key = this.displayTxHistoryFactory.createRowKeyForOrderTx(oTx);
            const existingIndex = this.getTableRowIndex(undefined, key, allRows);

            if (existingIndex < 0) {
                const row = this.displayTxHistoryFactory.create(oTx);
                const index = this.getRowSortedIndex(allRows, row);
                allRows.splice(index, 0, row);

                if (allRows.length >= this.recentRowLimit) {
                    break;
                }
            }
        }
    }

    private listenToTableEvents(): void {
        this.table.columnsUpdate$.pipe(
            switchMap(() => this.widgetImpl.updateSettings({
                columns: this.table.columnsConfig
            }))
        ).subscribe();
    }

    private listenToTableSize(): void {
        this.tableComponent.tableBodyEmptyRowDirective.tableAvailableRowsObserver.availableFullRowCount$.pipe(
            tap(availableRowCount => {
                if (this.$currentTab() === DexTxHistoryWidgetTab.HISTORY) {
                    this.tablePaginationComponent?.setPageSize(availableRowCount);
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private bindFiltersAndSettings(): void {
        const handleUserWallets$ = this.dexFilterOTxByUserWalletsService.getUserWalletAddressesStream().pipe(
            tap(addresses => {
                this.filterByAddress.updateUserWallets(addresses);
                this.filterRecentByAddress.updateUserWallets(addresses);
            })
        );

        this.widgetFilter.bindFiltersAndSettings().pipe(
            withLatestFrom(handleUserWallets$),
            tap(() => this.onFilterChange()),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private listenToStorage(): void {
        const updates$ = this.dexOrderTxSubjectService.orderTxUpdated$.pipe(
            squash({
                closingNotifier: WIDGET_UPDATES_GATING$,
                groupBy:         (oTx: DexOrderTx) => oTx.id,
                filterBy:        (oTx: DexOrderTx) => !!oTx.order || !!oTx.transaction,
                spread:          true
            }) as (source: Observable<DexOrderTx>) => Observable<DexOrderTx>,
            tap(oTx =>
                this.addOrUpdateSingle(oTx)
            )
        );

        updates$.pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private shareRowsFromRecentToHistory(): void {
        const shareOneDirection = (fromRows: DisplayTxHistoryRec[], toRows: DisplayTxHistoryRec[]): void => {
            if (fromRows?.length && toRows) {
                for (const row of fromRows) {
                    const index = this.getTableRowIndex(undefined, row.key, toRows);

                    if (index >= 0) {
                        toRows[index] = row;
                    }
                }
            }
        };

        shareOneDirection(this.recentRows, this.historyRows);
    }

}
